import { ProductVariant } from "../types/api";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import ButtonWithConfirm from "src/components/modal.confirm-dialog.component";
import { CreatePOLineItemModal } from "src/components/modal.create-polineitem";
import GlobalMessageContext from "src/context/globalMessage.context";
import { HomeRoute, ProductsRoute } from "src/routes";
import { updateVariantStockLevelAndPrices, auditStockAdjustment } from "src/services/product.service";
import DeleteIcon from "@mui/icons-material/Delete";
import { StyledTableCell, StyledTableRow } from "src/styles";
import { currencyFormatter } from "src/util";

const ADJUSTMENT_REASONS = {
  STOCKTAKE: "Stocktake",
  DAMAGE: "Damage",
  LOST_IN_TRANSIT: "Lost in transit",
};

type DataRow = {
  variantGuid: string;
  variantSku: string;
  productName: string;
  stockOnHand: number;
  initialDelta: number;
  delta: number;
  buyPrice: number;
  retailPrice: number;
  buyPriceStr: string;
  retailPriceStr: string;
  imageUrl: string;
  reason: string;
};

export const AdjustStockView = () => {
  const uxMessage = useContext(GlobalMessageContext);
  const navigate = useNavigate();
  const [rows, setRows] = useState<DataRow[]>([]);
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const addItem = (v: ProductVariant) => {
    const newRow: DataRow = {
      variantGuid: v.guid,
      variantSku: v.sku,
      productName: v.name || v.product?.name || "Unknown Product",
      stockOnHand: v.stockOnHand,
      initialDelta: 0,
      delta: 0,
      buyPrice: v.buyPrice,
      buyPriceStr: currencyFormatter.format(v.buyPrice / 100),
      retailPrice: v.retailPrice,
      retailPriceStr: currencyFormatter.format(v.retailPrice / 100),
      imageUrl: v.imageUrls && v.imageUrls.length > 0 ? v.imageUrls[0] : "",
      reason: "",
    };

    // Check we don't already have this in the list
    if (rows.filter((r) => r.variantSku === v.sku).length > 0) {
      uxMessage.setMessage({
        message: `${v.sku} already in the list!`,
        severity: "warning",
      });

      return;
    }

    if (newRow.stockOnHand < 0)
      newRow.initialDelta = newRow.delta = Math.abs(newRow.stockOnHand);

    setRows([...rows, newRow]);
  };

  const applyChanges = async () => {
    // Validate that all rows with stock adjustments have reasons
    const rowsWithAdjustments = rows.filter(r => r.delta !== 0);
    const invalidRows = rowsWithAdjustments.filter(r => !r.reason);
    
    if (invalidRows.length > 0) {
      uxMessage.setMessage({
        message: "Please provide a reason for all stock adjustments",
        severity: "error",
      });
      return;
    }

    try {
      for (const r of rows) {
        await updateVariantStockLevelAndPrices(
          r.variantGuid,
          r.delta,
          r.retailPrice,
          r.buyPrice
        );

        // Audit stock adjustment if there was a change
        if (r.delta !== 0) {
          await auditStockAdjustment(r.variantSku, r.delta, r.reason);
        }
      }

      uxMessage.setMessage({
        message: "Stock updated",
        severity: "success",
      });

      navigate(HomeRoute);
    } catch (error) {
      uxMessage.setMessage({
        message: "Failed to update stock",
        severity: "error",
      });
    }
  };

  const isApplyButtonDisabled = () => {
    if (_.isEmpty(rows)) return true;
    
    const rowsWithAdjustments = rows.filter(r => r.delta !== 0);
    const invalidRows = rowsWithAdjustments.filter(r => !r.reason);
    
    return invalidRows.length > 0;
  };

  const _onChange = (row: DataRow, e: any) => {
    const sku = row.variantSku;
    const field = e.target.name;

    const r = rows.filter((r) => r.variantSku === sku)[0];

    switch (field) {
      case "delta":
        r.delta =
          +e.target.value < -r.stockOnHand ? -r.stockOnHand : +e.target.value;
        // Clear reason if delta becomes 0
        if (r.delta === 0) {
          r.reason = "";
        }
        break;

      case "buyPrice":
        r.buyPrice = e.target.value.replace(/[^0-9]/g, "");
        r.buyPriceStr = currencyFormatter.format(r.buyPrice / 100);
        break;

      case "retailPrice":
        r.retailPrice = e.target.value.replace(/[^0-9]/g, "");
        r.retailPriceStr = currencyFormatter.format(r.retailPrice / 100);
        break;

      case "reason":
        r.reason = e.target.value;
        break;
    }

    setRows([...rows]);
  };

  const _deleteRow = (sku: string) => {
    setRows([...rows.filter((r) => r.variantSku !== sku)]);
  };

  return (
    <>
      <Typography variant="h1">Adjust Stock</Typography>
      <Box sx={{ m: 1 }}>
        <Grid container spacing={2}>
          <Grid size={2}/>
          <Grid size={8}>
          {_.isEmpty(rows) && (
        <>
          <p>
            Please use the "Add Variant" button below to add products to this
            list, then use the up and down arrows to set the required stock
            adjustment. You may also update the cost and retail price on this
            screen.
          </p>
        </>
      )}

          </Grid>
          <Grid size={2}/>
          <Grid size={4} sx={{ textAlign: "left" }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => navigate(ProductsRoute)}
              sx={{ margin: 1 }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid size={4}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setShowDialog(true)}
              sx={{ margin: 1 }}
            >
              Add Variant
            </Button>
            <CreatePOLineItemModal
              open={showDialog}
              onClose={() => setShowDialog(false)}
              onSelectVariant={(v: ProductVariant) => {
                addItem(v);
                setShowDialog(false);
              }}
            />
          </Grid>
          <Grid size={4} sx={{ textAlign: "right" }}>
            <ButtonWithConfirm
              variant="contained"
              color="primary"
              onCancel={() => setShowDialog(false)}
              onClose={() => setShowDialog(false)}
              onOk={() => applyChanges()}
              sx={{ margin: 1 }}
              message="This will update stock levels and prices instantly in AMS and Shopify."
              disabled={isApplyButtonDisabled()}
            >
              Apply Adjustments
            </ButtonWithConfirm>
          </Grid>

          <Grid size={12}>
            {!_.isEmpty(rows) && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell width={"5%"}></StyledTableCell>
                      <StyledTableCell width={"10%"}>SKU</StyledTableCell>
                      <StyledTableCell width={"25%"}>Product Name</StyledTableCell>
                      <StyledTableCell width={"8%"}>Available</StyledTableCell>
                      <StyledTableCell width={"8%"}>
                        Adjustment
                      </StyledTableCell>
                      <StyledTableCell width={"8%"}>
                        After Adjustment
                      </StyledTableCell>
                      <StyledTableCell width={"12%"}>Reason</StyledTableCell>
                      <StyledTableCell width={"8%"}>Buy Price</StyledTableCell>
                      <StyledTableCell width={"8%"}>
                        Retail Price
                      </StyledTableCell>
                      <StyledTableCell width={"8%"}>
                        Delete Row
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r: DataRow) => (
                      <StyledTableRow>
                        <TableCell>
                          {r.imageUrl ? (
                            <img
                              src={r.imageUrl}
                              alt={r.variantSku}
                              height="35px"
                              style={{ objectFit: 'cover' }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div style={{ width: '35px', height: '35px', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: '10px', color: '#999' }}>No Image</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{r.variantSku}</TableCell>
                        <TableCell>{r.productName}</TableCell>
                        <TableCell>{r.stockOnHand}</TableCell>
                        <TableCell>
                          <TextField
                            name="delta"
                            size="small"
                            type="number"
                            value={r.delta}
                            onChange={(e) => _onChange(r, e)}
                          />
                        </TableCell>
                        <TableCell>{r.stockOnHand + r.delta}</TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth disabled={r.delta === 0}>
                            <InputLabel>Reason</InputLabel>
                            <Select
                              name="reason"
                              value={r.reason}
                              label="Reason"
                              onChange={(e) => _onChange(r, e)}
                            >
                              {Object.entries(ADJUSTMENT_REASONS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                  {label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            name="buyPrice"
                            size="small"
                            value={r.buyPriceStr}
                            onChange={(e) => _onChange(r, e)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            name="retailPrice"
                            size="small"
                            value={r.retailPriceStr}
                            onChange={(e) => _onChange(r, e)}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => _deleteRow(r.variantSku)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
